import type {
  BackendResponse,
  OtpChallenge,
  OtpConfig,
  OtpRequestInput,
  OtpVerified,
  OtpVerifyInput,
  SessionInfo,
  SessionPayload,
} from "@/features/auth/types";
import { getDeviceId } from "@/shared/lib/device-id";
import { attributionSuffix } from "@/shared/lib/attribution";
import { nativePlatform } from "@/features/capture/scan";

import { API_ORIGIN, apiUrl } from "../client";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

/**
 * Auth API endpoints matching the backend at /api/v1/auth/*.
 *
 * Google OAuth uses a full-page navigation to the backend's login URL because
 * the backend responds with a 302 redirect to accounts.google.com — fetch()
 * would auto-follow that cross-origin redirect and get blocked by CORS.
 * Microsoft OAuth returns JSON with the auth URL, so fetch is fine there.
 * Session validation and logout go through the Vite proxy.
 */
export class AccountDeletionError extends Error {
  constructor(public code: string, message: string) {
    super(message);
    this.name = "AccountDeletionError";
  }
}

export const authApi = {
  /**
   * Validate the current session and return the user profile + expiry.
   * Returns null if the session is invalid (401).
   */
  getSession: async (sessionToken?: string): Promise<SessionInfo | null> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (sessionToken) {
      headers["X-Session-Token"] = sessionToken;
    }

    const res = await fetch(apiUrl("/v1/auth/session"), {
      credentials: "include",
      headers,
      // 15s, matching the OTP calls. It was 5s, which is a desk-broadband
      // number: this request decides whether the operator is signed in, and
      // on a factory floor it runs over whatever signal the building has.
      // Measured at 1.9s from a good connection; a phone at 0.5 KB/s blows
      // through 5s and the app then shows a login screen to somebody who is
      // already signed in, who signs in again, who mints another session.
      // Observed on a real handset with three live sessions to show for it.
      signal: AbortSignal.timeout(SESSION_TIMEOUT_MS),
    });

    if (res.status === 401) return null;

    if (!res.ok) {
      throw new Error(`Session check failed: ${res.statusText}`);
    }

    const body: BackendResponse<SessionPayload> = await res.json();
    if (!body.data) return null;

    const {
      expires_at,
      org_id,
      workspace_email,
      is_workspace_admin,
      org_role,
      is_platform_admin,
      features,
      feature_roles,
      session_token,
      ...rest
    } = body.data;
    return {
      userProfile: {
        ...rest,
        orgId: org_id,
        workspaceEmail: workspace_email,
        isWorkspaceAdmin: is_workspace_admin,
        // `?? null` and `?? false`, not a default of true: a backend that does
        // not send these yet must leave the decision to isWorkspaceAdmin rather
        // than silently widening or narrowing who gets in.
        orgRole: org_role ?? null,
        isPlatformAdmin: is_platform_admin ?? false,
        features: features ?? [],
        featureRoles: feature_roles ?? {},
      },
      expiresAt: expires_at ? new Date(expires_at) : null,
      sessionToken: session_token ?? null,
    };
  },

  /**
   * Log out the current user. Clears the session on the backend.
   */
  logout: async (sessionToken?: string): Promise<void> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (sessionToken) {
      headers["X-Session-Token"] = sessionToken;
    }

    await fetch(apiUrl("/v1/auth/logout"), {
      method: "POST",
      credentials: "include",
      headers,
    });
  },

  /**
   * Permanently delete the signed-in user's account (App Store Guideline
   * 5.1.1(v)). Resolves once the backend has deleted it; every session dies
   * with it. Throws AccountDeletionError with the backend's stable `code`
   * (`last_admin`, `platform_admin`) when it refuses, so the caller can say
   * what to do next instead of a generic failure.
   */
  deleteAccount: async (sessionToken?: string): Promise<void> => {
    const headers: Record<string, string> = {};
    if (sessionToken) {
      headers["X-Session-Token"] = sessionToken;
    }
    const res = await fetch(apiUrl("/v1/auth/account"), {
      method: "DELETE",
      credentials: "include",
      headers,
    });
    if (res.ok) return;
    let code = "unknown";
    let message = "Could not delete your account. Try again.";
    try {
      const body = await res.json();
      const detail = body?.detail;
      if (detail && typeof detail === "object") {
        code = detail.code ?? code;
        message = detail.message ?? message;
      }
    } catch {
      // Non-JSON error body (edge 5xx): keep the generic message.
    }
    if (res.status === 401) code = "unauthenticated";
    throw new AccountDeletionError(code, message);
  },

  getGoogleLoginUrl: (): string => {
    // 127.0.0.1 → localhost so the session cookie set on /login/google is sent
    // back on the /auth/google callback (Google's registered redirect_uri uses
    // localhost). Mismatched host = CSRF state mismatch.
    const base = (API_BASE_URL || API_ORIGIN).replace("127.0.0.1", "localhost");
    const origin = encodeURIComponent(window.location.origin);
    return `${base}/api/v1/auth/login/google?redirect_uri=${origin}${attributionSuffix()}`;
  },

  getMicrosoftLoginUrl: async (): Promise<string> => {
    const origin = encodeURIComponent(window.location.origin);
    const res = await fetch(apiUrl(`/v1/auth/login/microsoft?redirect_uri=${origin}${attributionSuffix()}`), {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) throw new Error("Failed to get Microsoft login URL");
    const body: BackendResponse<{ auth_url: string }> = await res.json();
    if (!body.data?.auth_url) throw new Error("No auth URL in response");
    return body.data.auth_url;
  },

  getAppleLoginUrl: (): string => {
    const base = (API_BASE_URL || API_ORIGIN).replace("127.0.0.1", "localhost");
    const origin = encodeURIComponent(window.location.origin);
    return `${base}/api/v1/auth/login/apple?redirect_uri=${origin}${attributionSuffix()}`;
  },

  /**
   * Which optional providers this deployment offers. FAIL CLOSED, like
   * otpApi.config: any error means "none", so an unreachable backend never
   * draws a button that dead-ends. A server switch rather than a VITE_ flag
   * because the mobile bundle has no OTA channel.
   */
  getLoginProviders: async (): Promise<{ apple: boolean }> => {
    try {
      const res = await fetch(apiUrl("/v1/auth/providers"), {
        signal: AbortSignal.timeout(5_000),
      });
      if (!res.ok) return { apple: false };
      const body: BackendResponse<{ apple?: boolean }> = await res.json();
      return { apple: body.data?.apple === true };
    } catch {
      return { apple: false };
    }
  },
};

/* ── Phone + OTP sign-in ────────────────────────────────────────────────────
 *
 * These three calls use RAW `fetch`, not the `api` helper, and that is
 * load-bearing rather than stylistic.
 *
 * `api.post` attaches `_sessionToken` to every request. On a SHARED FLOOR
 * PHONE that token is very often the PREVIOUS operator's, still sitting in
 * localStorage because they closed the app rather than signing out. An OTP
 * request carrying a stale session is ambiguous at the backend — is this a
 * signed-in user or an anonymous one? — and the one thing this endpoint must
 * be is unambiguously unauthenticated. So: no client, no token, no
 * credentials.
 *
 * `device_id` travels in the BODY, never in a header. The edge pins the
 * allowed request headers to exactly "X-Session-Token, X-Request-ID,
 * Content-Type" (see the /api/v1/otp/ location block in nginx.conf), so an
 * X-Device-Id header would be dropped by CORS preflight — and the failure is
 * an opaque "network error" on device only, with nothing in our logs.
 *
 * The timeout is 15s, three times `getSession`'s 5s: a send is not a database
 * read, it is a wait on a carrier's SMS gateway.
 */

const OTP_TIMEOUT_MS = 15_000;
/** The session check gets the same budget as an OTP send: both are
 *  "can this phone reach Ubik at all", not "is the database quick". */
const SESSION_TIMEOUT_MS = 15_000;

/** A typed OTP failure. `code` is the backend's machine-readable error code
 *  (otp_expired, otp_locked, …) — the ONLY thing callers should branch on. */
export class OtpError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly retryAfterSeconds: number | null;
  readonly attemptsRemaining: number | null;

  constructor(
    status: number,
    code: string | null,
    message: string,
    extra?: { retryAfterSeconds?: number | null; attemptsRemaining?: number | null },
  ) {
    super(message);
    this.name = "OtpError";
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = extra?.retryAfterSeconds ?? null;
    this.attemptsRemaining = extra?.attemptsRemaining ?? null;
  }
}

function numberOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * One place that turns an OTP response into either `data` or an OtpError.
 *
 * Every read here is defensive on purpose: `strictNullChecks` is OFF in
 * tsconfig.app.json, so widening a type produces no compiler error and tsc
 * cannot find a null dereference in this file for us. `detail` may be a
 * string (FastAPI's default), an object (our structured errors), an array
 * (FastAPI's own 422 validation shape) or absent.
 */
async function otpFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      ...init,
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      signal: AbortSignal.timeout(OTP_TIMEOUT_MS),
    });
  } catch (err) {
    // Offline, DNS, TLS, or the 15s timeout. No `code` — the caller renders
    // the generic "couldn't reach Ubik" copy.
    throw new OtpError(0, null, err instanceof Error ? err.message : "Network error");
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  const record = (body && typeof body === "object" ? body : {}) as Record<string, unknown>;

  if (!res.ok) {
    const rawDetail = record.detail;
    const detail =
      rawDetail && typeof rawDetail === "object" && !Array.isArray(rawDetail)
        ? (rawDetail as Record<string, unknown>)
        : {};
    const code =
      typeof detail.code === "string"
        ? detail.code
        : typeof record.code === "string"
          ? record.code
          : null;
    const message =
      typeof detail.message === "string"
        ? detail.message
        : typeof rawDetail === "string"
          ? rawDetail
          : `HTTP ${res.status}`;
    throw new OtpError(res.status, code, message, {
      retryAfterSeconds: numberOrNull(detail.retry_after_seconds),
      attemptsRemaining: numberOrNull(detail.attempts_remaining),
    });
  }

  const envelope = record as unknown as BackendResponse<T>;
  if (!envelope || envelope.data == null) {
    throw new OtpError(res.status, null, "Malformed response from Ubik");
  }
  return envelope.data;
}

export const otpApi = {
  /**
   * The gate for the whole entry point. FAIL CLOSED: any throw here means the
   * caller hides phone sign-in rather than showing a button that goes nowhere.
   * Deliberately NOT a build-time VITE_ flag — the mobile bundle has no OTA
   * channel, so a compiled-in flag would be frozen until the next store
   * release, and this switch has to be reachable from the server.
   */
  config: (): Promise<OtpConfig> => otpFetch<OtpConfig>("/v1/otp/config"),

  /**
   * Send a one-time code. THIS IS ALSO THE RESEND — the backend has no second
   * endpoint; calling it again with the same number issues a fresh challenge.
   */
  request: (input: OtpRequestInput): Promise<OtpChallenge> =>
    otpFetch<OtpChallenge>("/v1/otp/request", {
      method: "POST",
      body: JSON.stringify({
        phone: input.phone,
        country: input.country,
        ...(input.channel ? { channel: input.channel } : {}),
        ...(input.locale ? { locale: input.locale } : {}),
        device_id: getDeviceId(),
        platform: nativePlatform(),
      }),
    }),

  /** Exchange a code for a session token. */
  verify: (input: OtpVerifyInput): Promise<OtpVerified> =>
    otpFetch<OtpVerified>("/v1/otp/verify", {
      method: "POST",
      body: JSON.stringify({
        challenge_id: input.challenge_id,
        code: input.code,
        device_id: getDeviceId(),
        platform: nativePlatform(),
      }),
    }),
};
